import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMapPin, FiChevronDown, FiSearch, FiStar } = FiIcons;

const StateDropdown = ({ buttonText = "Find Your State Resources", className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const navigate = useNavigate();

  const states = [
    { name: 'Alabama', code: 'AL', slug: 'alabama', hasDetailedData: true },
    { name: 'Alaska', code: 'AK', slug: 'alaska', hasDetailedData: true },
    { name: 'Arizona', code: 'AZ', slug: 'arizona', hasDetailedData: true },
    { name: 'Arkansas', code: 'AR', slug: 'arkansas', hasDetailedData: true },
    { name: 'California', code: 'CA', slug: 'california', hasDetailedData: true },
    { name: 'Colorado', code: 'CO', slug: 'colorado', hasDetailedData: true },
    { name: 'Connecticut', code: 'CT', slug: 'connecticut', hasDetailedData: true },
    { name: 'Delaware', code: 'DE', slug: 'delaware', hasDetailedData: true },
    { name: 'Florida', code: 'FL', slug: 'florida', hasDetailedData: true },
    { name: 'Georgia', code: 'GA', slug: 'georgia', hasDetailedData: true },
    { name: 'Hawaii', code: 'HI', slug: 'hawaii', hasDetailedData: true },
    { name: 'Idaho', code: 'ID', slug: 'idaho', hasDetailedData: true },
    { name: 'Illinois', code: 'IL', slug: 'illinois', hasDetailedData: true },
    { name: 'Indiana', code: 'IN', slug: 'indiana', hasDetailedData: true },
    { name: 'Iowa', code: 'IA', slug: 'iowa', hasDetailedData: true },
    { name: 'Kansas', code: 'KS', slug: 'kansas', hasDetailedData: true },
    { name: 'Kentucky', code: 'KY', slug: 'kentucky', hasDetailedData: true },
    { name: 'Louisiana', code: 'LA', slug: 'louisiana', hasDetailedData: true },
    { name: 'Maine', code: 'ME', slug: 'maine', hasDetailedData: true },
    { name: 'Maryland', code: 'MD', slug: 'maryland', hasDetailedData: true },
    { name: 'Massachusetts', code: 'MA', slug: 'massachusetts', hasDetailedData: true },
    { name: 'Michigan', code: 'MI', slug: 'michigan', hasDetailedData: true },
    { name: 'Minnesota', code: 'MN', slug: 'minnesota', hasDetailedData: true },
    { name: 'Mississippi', code: 'MS', slug: 'mississippi', hasDetailedData: true },
    { name: 'Missouri', code: 'MO', slug: 'missouri', hasDetailedData: true },
    { name: 'Montana', code: 'MT', slug: 'montana', hasDetailedData: true },
    { name: 'Nebraska', code: 'NE', slug: 'nebraska', hasDetailedData: true },
    { name: 'Nevada', code: 'NV', slug: 'nevada', hasDetailedData: true },
    { name: 'New Hampshire', code: 'NH', slug: 'new-hampshire', hasDetailedData: true },
    { name: 'New Jersey', code: 'NJ', slug: 'new-jersey', hasDetailedData: true },
    { name: 'New Mexico', code: 'NM', slug: 'new-mexico', hasDetailedData: true },
    { name: 'New York', code: 'NY', slug: 'new-york', hasDetailedData: true },
    { name: 'North Carolina', code: 'NC', slug: 'north-carolina', hasDetailedData: true },
    { name: 'North Dakota', code: 'ND', slug: 'north-dakota', hasDetailedData: true },
    { name: 'Ohio', code: 'OH', slug: 'ohio', hasDetailedData: true },
    { name: 'Oklahoma', code: 'OK', slug: 'oklahoma', hasDetailedData: true },
    { name: 'Oregon', code: 'OR', slug: 'oregon', hasDetailedData: true },
    { name: 'Pennsylvania', code: 'PA', slug: 'pennsylvania', hasDetailedData: true },
    { name: 'Rhode Island', code: 'RI', slug: 'rhode-island', hasDetailedData: true },
    { name: 'South Carolina', code: 'SC', slug: 'south-carolina', hasDetailedData: true },
    { name: 'South Dakota', code: 'SD', slug: 'south-dakota', hasDetailedData: true },
    { name: 'Tennessee', code: 'TN', slug: 'tennessee', hasDetailedData: true },
    { name: 'Texas', code: 'TX', slug: 'texas', hasDetailedData: true },
    { name: 'Utah', code: 'UT', slug: 'utah', hasDetailedData: true },
    { name: 'Vermont', code: 'VT', slug: 'vermont', hasDetailedData: true },
    { name: 'Virginia', code: 'VA', slug: 'virginia', hasDetailedData: true },
    { name: 'Washington', code: 'WA', slug: 'washington', hasDetailedData: true },
    { name: 'Washington DC', code: 'DC', slug: 'washington-dc', hasDetailedData: true },
    { name: 'West Virginia', code: 'WV', slug: 'west-virginia', hasDetailedData: true },
    { name: 'Wisconsin', code: 'WI', slug: 'wisconsin', hasDetailedData: true },
    { name: 'Wyoming', code: 'WY', slug: 'wyoming', hasDetailedData: true }
  ];

  // Filter states based on search term
  const filteredStates = states.filter(state => 
    state.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    state.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Separate states with detailed data and put them first
  const sortedStates = filteredStates.sort((a, b) => {
    if (a.hasDetailedData && !b.hasDetailedData) return -1;
    if (!a.hasDetailedData && b.hasDetailedData) return 1;
    return a.name.localeCompare(b.name);
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleStateSelect = (state) => {
    navigate(`/states/${state.slug}`);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleViewAllStates = () => {
    navigate('/states');
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center px-8 py-3 bg-primary-700 text-white font-semibold rounded-lg hover:bg-primary-800 transition-colors shadow-lg border-2 border-primary-600 w-full sm:w-auto justify-center"
      >
        <SafeIcon icon={FiMapPin} className="mr-2" />
        {buttonText}
        <SafeIcon
          icon={FiChevronDown}
          className={`ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 mt-2 w-full sm:w-96 bg-white rounded-lg shadow-xl border border-primary-200 z-50 max-h-96 flex flex-col"
          >
            {/* Search Input */}
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <SafeIcon
                  icon={FiSearch}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search states..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* States List */}
            <div className="flex-1 overflow-y-auto">
              {sortedStates.length > 0 ? (
                <div className="py-2">
                  {sortedStates.map((state) => (
                    <button
                      key={state.slug}
                      onClick={() => handleStateSelect(state)}
                      className="w-full px-4 py-3 text-left hover:bg-primary-50 transition-colors flex items-center justify-between group"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900 group-hover:text-primary-700">
                            {state.name}
                          </span>
                          <span className="text-sm text-gray-500">({state.code})</span>
                        </div>
                        {state.hasDetailedData && (
                          <SafeIcon icon={FiStar} className="text-primary-500 text-sm" />
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {state.hasDetailedData ? (
                          <span className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded-full font-medium">
                            Complete Guide
                          </span>
                        ) : (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full font-medium">
                            Coming Soon
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="py-8 px-4 text-center text-gray-500">
                  <p>No states found matching "{searchTerm}"</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4">
              <button
                onClick={handleViewAllStates}
                className="w-full text-center text-primary-600 hover:text-primary-700 font-medium text-sm py-2 hover:bg-primary-50 rounded-md transition-colors"
              >
                View All States & Resources
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StateDropdown;